/**
 * AddEditPromoBannerV2 — V2 port of the Promo Banner edit form.
 * Same Redux fetch / submit graph as v1.
 *
 * Sections:
 *   01 Banner details   — title / description + Active + Winback toggles
 *   02 Banner images    — mobile banner / wide banner / icon UploadTiles
 *   03 Danger zone (edit-only)
 *
 * v1 exposed `validateAndGetData()` via ref because the page chrome
 * dispatched the save itself. V2 inverts that — the form owns
 * `submit()` (validate → dispatch addEditPromoBanner) and the page
 * fires it via the native form="promo-banner-form" + type="submit"
 * pairing, same as every other V2 edit page.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import {
  fetchOnePromoBanner,
  addEditPromoBanner,
  deletePromoBanner,
} from '../../../actions/promoBannerActions';
import { uploadImage, clearMedia } from '../../../actions/mediaActions';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  ToggleSwitch,
  UploadTile,
} from '../../redesign/edit';

const AddEditPromoBannerV2 = forwardRef(function AddEditPromoBannerV2(
  { isEdit, selectedId, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    promo_banner_url: '',
    promo_banner_title: '',
    promo_banner_text: '',
    promo_banner_is_active: 0,
    promo_banner_icon: '',
    promo_banner_image_wide: '',
    is_winback: 0,
  });

  const [previewData, setPreviewData] = useState({
    isSetToEdit: false,
    bannerPreview: null,
    iconPreview: null,
    wideBannerPreview: null,
  });

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  /* ─── Fetch on mount (1:1 with v1) ─────────────────────────── */
  useEffect(() => {
    if (!isEdit || !selectedId || previewData.isSetToEdit) return;
    setLoading(true);
    dispatch(fetchOnePromoBanner(selectedId))
      .then((bannerData) => {
        setFormData({ ...bannerData });
        setPreviewData((prev) => ({ ...prev, isSetToEdit: true }));
        if (onLoadedRef.current) {
          onLoadedRef.current({
            promo_banner_title: bannerData?.promo_banner_title,
            promo_banner_updated_at:
              bannerData?.promo_banner_updated_at
              ?? bannerData?.updated_at
              ?? null,
          });
        }
      })
      .catch(() => {
        Swal.fire({ icon: 'warning', text: 'Error finding selected Promo Banner to edit' });
      })
      .finally(() => setLoading(false));
  }, [dispatch, isEdit, selectedId, previewData.isSetToEdit]);

  /* Preview-URL cleanup on unmount (1:1 with v1, via ref so the
   * cleanup reads the current URLs, not stale closures). */
  const previewDataRef = useRef(previewData);
  previewDataRef.current = previewData;
  useEffect(() => () => {
    const { bannerPreview, iconPreview, wideBannerPreview } = previewDataRef.current;
    if (bannerPreview) window.URL.revokeObjectURL(bannerPreview);
    if (iconPreview) window.URL.revokeObjectURL(iconPreview);
    if (wideBannerPreview) window.URL.revokeObjectURL(wideBannerPreview);
    dispatch(clearMedia());
  }, [dispatch]);

  /* ─── Handlers ─────────────────────────────────────────────── */
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (name) => (e) =>
    setFormData((prev) => ({ ...prev, [name]: e.target.checked ? 1 : 0 }));

  /**
   * Single upload helper for all three banner images. Reads the
   * preview URL slot + formData URL slot off the `slots` map so
   * we don't duplicate the same boilerplate three times.
   */
  const slots = {
    banner:    { preview: 'bannerPreview',     field: 'promo_banner_url' },
    wide:      { preview: 'wideBannerPreview', field: 'promo_banner_image_wide' },
    icon:      { preview: 'iconPreview',       field: 'promo_banner_icon' },
  };
  const uploadFor = (slotKey) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { preview, field } = slots[slotKey];
    setPreviewData((prev) => {
      if (prev[preview]) window.URL.revokeObjectURL(prev[preview]);
      return { ...prev, [preview]: window.URL.createObjectURL(file) };
    });
    dispatch(uploadImage(file)).then((response) => {
      setFormData((prev) => ({ ...prev, [field]: response.data.image_url }));
      dispatch(clearMedia());
    });
  };
  const removeFor = (slotKey) => () => {
    const { preview, field } = slots[slotKey];
    setPreviewData((prev) => {
      if (prev[preview]) window.URL.revokeObjectURL(prev[preview]);
      return { ...prev, [preview]: null };
    });
    setFormData((prev) => ({ ...prev, [field]: '' }));
  };

  /* ─── Submit (1:1 with v1) ──────────────────────────────── */
  const submit = useCallback(() => {
    if (!formData.promo_banner_url) {
      Swal.fire({ icon: 'error', text: 'You must upload a Promo Banner image.' });
      return;
    }
    setIsSaving(true);
    const result = dispatch(addEditPromoBanner(
      JSON.stringify(formData),
      () => navigate('/banners'),
    ));
    if (result && typeof result.finally === 'function') {
      result.finally(() => setIsSaving(false));
    } else {
      setTimeout(() => setIsSaving(false), 3000);
    }
  }, [formData, dispatch, navigate]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submit();
  };

  useImperativeHandle(ref, () => ({
    submit,
    // Keep v1's API alive so any external caller still works.
    validateAndGetData: () => {
      if (!formData.promo_banner_url) {
        Swal.fire({ icon: 'error', text: 'You must upload a Promo Banner image.' });
        return false;
      }
      return formData;
    },
  }), [submit, formData]);

  /* ─── Delete (1:1 with v1) ──────────────────────────────── */
  const handleDelete = () => {
    Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this Banner?',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Delete Item',
    }).then((result) => {
      if (result.value) {
        dispatch(deletePromoBanner(selectedId, () => navigate('/banners')));
      }
    });
  };

  if (isEdit && loading) {
    return <LoadingBackdrop open />;
  }

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <form
        id="promo-banner-form"
        onSubmit={handleSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
        style={{ minWidth: 0, maxWidth: 980 }}
      >
        {/* 01 Banner details */}
        <FormSection title="Banner details" num="01">
          <FieldBlock label="Banner title" htmlFor="f-pb-title">
            <TextInput
              id="f-pb-title"
              name="promo_banner_title"
              value={formData.promo_banner_title || ''}
              onChange={handleFieldChange}
            />
          </FieldBlock>
          <FieldBlock label="Banner description" htmlFor="f-pb-text">
            <Textarea
              id="f-pb-text"
              name="promo_banner_text"
              value={formData.promo_banner_text || ''}
              onChange={handleFieldChange}
              rows={4}
              placeholder="Body copy shown alongside the banner image."
            />
          </FieldBlock>
          <FieldBlock label="Status" noMargin>
            <div className="flex flex-wrap" style={{ gap: '14px 28px' }}>
              <ToggleSwitch
                name="promo_banner_is_active"
                checked={!!formData.promo_banner_is_active}
                onChange={handleToggle('promo_banner_is_active')}
                label="Active"
              />
              <ToggleSwitch
                name="is_winback"
                checked={!!formData.is_winback}
                onChange={handleToggle('is_winback')}
                label="Winback"
                hint="Shown to users who recently cancelled"
              />
            </div>
          </FieldBlock>
        </FormSection>

        {/* 02 Banner images */}
        <FormSection title="Banner images" num="02">
          <FieldRow cols={3} minColWidth={200}>
            <UploadTile
              label="Banner image (mobile)"
              required
              hint="Shown in the mobile app"
              value={previewData.bannerPreview || formData.promo_banner_url}
              accept="image/*"
              onChange={uploadFor('banner')}
              onRemove={removeFor('banner')}
            />
            <UploadTile
              label="Banner image (web)"
              hint="Wide variant for desktop"
              value={previewData.wideBannerPreview || formData.promo_banner_image_wide}
              accept="image/*"
              onChange={uploadFor('wide')}
              onRemove={removeFor('wide')}
            />
            <UploadTile
              label="Icon image"
              hint="Small accompanying glyph"
              value={previewData.iconPreview || formData.promo_banner_icon}
              accept="image/*"
              onChange={uploadFor('icon')}
              onRemove={removeFor('icon')}
            />
          </FieldRow>
        </FormSection>

        {/* 03 Danger zone */}
        {isEdit && (
          <FormSection title="Danger zone" num="03">
            <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Button variant="danger" onClick={handleDelete} type="button">
                Delete banner
              </Button>
              <span className="text-text-muted" style={{ fontSize: 12 }}>
                Permanent — cannot be undone.
              </span>
            </div>
          </FormSection>
        )}
      </form>
    </>
  );
});

export default AddEditPromoBannerV2;
